import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowUpRight, ArrowRight, Play } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import CreativeHeroPipeline from "../components/CreativeHeroPipeline";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /creative — Sunday Digital
   An editorial film-magazine treatment of the Creative & Design
   service line. Carries the homepage DNA (yellow accent, mask
   reveals, marquees) but cranks the kinetics — full-bleed video
   hero, horizontal pinned scroll, asymmetric possibilities lab,
   parallax service panels, project trophy tiles, cross-page
   navigator that closes the loop with the rest of the site.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

const GhostCTA = ({ children, href, onClick, testId }) => {
  const className =
    "group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors";
  if (href) {
    return (
      <a href={href} className={className} data-testid={testId}>
        {children}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className} data-testid={testId}>
      {children}
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
};

/* ============================================================
   01 · Cinematic Hero — full-bleed loop video + vertical word
   morph. The three core verbs (Connect / Captivate / Convert)
   stack vertically and cycle on a 3.4s rhythm with a mask wipe.
============================================================ */
const HERO_VIDEO =
  "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";

const HERO_VERBS = ["Connect", "Captivate", "Convert"];

const CreativeHero = ({ onPrimary }) => {
  const cms = useContent("creative");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "Creative & Design";
  const eyebrow = h.eyebrow || "Ecommerce design · UI / UX · Brand systems";
  const subtitle = h.subtitle ||
    "Responsive UI, optimized visual systems, and high-impact creative for ecommerce — tailored to enhance usability, streamline interaction, and elevate every brand touchpoint.";
  const ctaLabel = h.cta_primary_label || h.cta_label || "Get a free consultation";
  const secondaryLabel = h.cta_secondary_label || "See solutions";
  const secondaryHref = h.cta_secondary_href || "#tailored";
  const video = h.video || HERO_VIDEO;
  const poster = h.poster ||
    "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=2000&q=70";
  const line1 = h.title_line_1 || "We design stores";
  const line2 = h.title_line_2 || "built to";
  const sideCaption = h.side_caption || "Reel · 2025 / 26";
  const liveVerbs = Array.isArray(h.verbs) && h.verbs.length
    ? h.verbs.map((v) => v.title || "").filter(Boolean)
    : HERO_VERBS;
  const liveMarquee = Array.isArray(h.marquee_words) && h.marquee_words.length
    ? h.marquee_words.map((v) => v.title || "").filter(Boolean)
    : ["Connect", "Captivate", "Convert", "Connect", "Captivate", "Convert"];
  const [verbIdx, setVerbIdx] = useState(0);
  useEffect(() => {
    if (liveVerbs.length < 2) return;
    const id = window.setInterval(
      () => setVerbIdx((i) => (i + 1) % liveVerbs.length),
      3400
    );
    return () => window.clearInterval(id);
  }, [liveVerbs.length]);
  // Clamp verbIdx when list shrinks
  useEffect(() => {
    if (verbIdx >= liveVerbs.length) setVerbIdx(0);
  }, [verbIdx, liveVerbs.length]);

  return (
    <section
      id="top"
      data-testid="creative-hero"
      className="relative overflow-hidden bg-[#0B0F14] min-h-[100svh] flex flex-col"
    >
      {/* Full-bleed creative pipeline — replaces the old hero video */}
      <div className="absolute inset-0">
        <CreativeHeroPipeline />
        {/* Cinematic dual-grade overlays (kept) — bottom darken so the
            big white headline + breadcrumb stay legible. The top is
            kept lighter so the workspace animation remains visible. */}
        <span
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/35 via-[#0B0F14]/20 to-[#0B0F14]/96"
        />
        <span
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/95 via-[#0B0F14]/45 to-[#0B0F14]/30"
        />
        {/* Lower-left vignette — guarantees the headline + paragraph
            + CTAs always sit on a near-solid backdrop, regardless of
            which stage is running. */}
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60rem 36rem at 18% 78%, rgba(11,15,20,0.85), rgba(11,15,20,0) 65%)",
          }}
        />
        {/* Yellow ambient flare */}
        <span
          aria-hidden
          className="absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full bg-[#F4D64E]/[0.10] blur-3xl"
        />
        {/* Subtle film grain */}
        <span
          aria-hidden
          className="absolute inset-0 opacity-[0.20] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.07) 1px, transparent 0)",
            backgroundSize: "3px 3px",
          }}
        />
      </div>

      {/* Top breadcrumb */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 w-full">
        <div
          data-reveal="eyebrow"
          className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-400"
        >
          <Link to="/" className="hover:text-[#F4D64E] transition-colors">
            Sunday
          </Link>
          <span className="w-1 h-1 rounded-full bg-neutral-600" />
          <span className="text-[#F4D64E]">{breadcrumb}</span>
        </div>
      </div>

      {/* Main composition */}
      <div className="relative flex-1 max-w-7xl w-full mx-auto px-6 md:px-10 pb-32 md:pb-40 flex items-end">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-end">
          <div className="lg:col-span-9">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>

            {/* Headline — vertical morph word */}
            <h1
              data-testid="creative-hero-title"
              className="font-display mt-7 text-white leading-[0.92] tracking-[-0.04em] font-black text-[2.75rem] sm:text-[4rem] md:text-[5.5rem] lg:text-[7rem] xl:text-[8rem]"
            >
              <span data-reveal="mask-block">
                <span>{line1}</span>
              </span>
              <span
                data-reveal="mask-block"
                style={{ "--i": 1 }}
                className="block"
              >
                <span>
                  {line2}{" "}
                  <span
                    aria-hidden
                    className="relative inline-block align-baseline"
                    style={{ minWidth: "5ch" }}
                  >
                    {liveVerbs.map((verb, i) => (
                      <span
                        key={`${verb}-${i}`}
                        className={`absolute left-0 top-0 italic font-normal text-[#F4D64E] transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          i === verbIdx
                            ? "opacity-100 translate-y-0"
                            : i === (verbIdx - 1 + liveVerbs.length) % liveVerbs.length
                            ? "opacity-0 -translate-y-full"
                            : "opacity-0 translate-y-full"
                        }`}
                      >
                        {verb}.
                      </span>
                    ))}
                    {/* Spacer to hold width */}
                    <span className="invisible">Captivate.</span>
                  </span>
                  <span aria-live="polite" className="sr-only">
                    {liveVerbs[verbIdx] || ""}
                  </span>
                </span>
              </span>
            </h1>

            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-9 max-w-[58ch] text-[1.05rem] md:text-[1.2rem] text-neutral-300 leading-[1.6]"
            >
              <span>
                {subtitle}
              </span>
            </p>

            <div
              data-reveal="eyebrow"
              style={{ "--i": 3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryCTA testId="creative-hero-cta" onClick={onPrimary}>
                {ctaLabel}
              </PrimaryCTA>
              <GhostCTA href={secondaryHref} testId="creative-hero-secondary">
                {secondaryLabel}
              </GhostCTA>
            </div>
          </div>

          {/* Right-side ribbon — vertical caption */}
          <div className="hidden lg:block lg:col-span-3 self-end justify-self-end pb-2">
            <div
              data-reveal="eyebrow"
              style={{ "--i": 4 }}
              className="flex items-center gap-3 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-400 [writing-mode:vertical-rl] rotate-180"
            >
              <span className="w-12 h-px bg-[#F4D64E]" />
              <span>{sideCaption}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom marquee pill — connects to homepage marquee grammar */}
      <div className="relative border-t border-white/[0.08] bg-[#0B0F14]/65 backdrop-blur-md">
        <div className="overflow-hidden">
          <motion.div
            className="flex items-center gap-12 py-4 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 32, ease: "linear", repeat: Infinity }}
          >
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex items-center gap-12 shrink-0">
                {liveMarquee.map(
                  (w, i) => (
                    <span
                      key={`${dup}-${i}`}
                      className="flex items-center gap-12"
                    >
                      <span className="font-display text-[1.3rem] md:text-[1.7rem] font-black tracking-[-0.015em] text-white uppercase">
                        {w}
                      </span>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F4D64E]" />
                    </span>
                  )
                )}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   02 · Tailored Solutions — horizontal pinned scroll reel.
   The signature "wow" moment. The section pins for ~3.5x its
   own height while a horizontal track translates left, exposing
   four full-screen editorial panels.
============================================================ */
const SOLUTIONS = [
  {
    n: "01",
    eyebrow: "Tailored Solutions",
    title: "Shopify Plus, custom-built",
    body: "Designs built for brands that demand more — more power, more flexibility, more results. Tailored features, seamless integrations, and advanced functionality to handle whatever you throw at it.",
    tag: "No limits, just results.",
    img: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1800&q=70",
  },
  {
    n: "02",
    eyebrow: "Tailored Solutions",
    title: "Shopify website design",
    body: "Look sharp and sell hard — pixel-perfect layouts, smart typography, and color systems that don't just match your brand, they amplify it. Built for engagement.",
    tag: "Beautiful. Intuitive. Built to move product.",
    img: "https://images.unsplash.com/photo-1545239351-cefce43f1e73?auto=format&fit=crop&w=1800&q=70",
  },
  {
    n: "03",
    eyebrow: "Tailored Solutions",
    title: "UX design",
    body: "UX is how your store works. We design seamless, intuitive shopping experiences that guide users from entry to checkout — fast. Smart structure, smooth navigation, zero guesswork.",
    tag: "Designing the path to purchase.",
    img: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?auto=format&fit=crop&w=1800&q=70",
  },
  {
    n: "04",
    eyebrow: "Tailored Solutions",
    title: "Interaction design",
    body: "Beyond aesthetics — high-performing, ecommerce-optimized interfaces with precision layouts, scalable components, and responsive design at the core. Every pixel engineered to keep users engaged.",
    tag: "Precision UI for high-performance stores.",
    img: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&w=1800&q=70",
  },
];

const TailoredSolutions = () => {
  const ref = useRef(null);
  const cms = useContent("creative");
  const ts = cms?.tailored_solutions || {};
  const progressLabel = ts.progress_label || "Reel — Tailored solutions";
  const liveSolutions = Array.isArray(ts.items) && ts.items.length
    ? ts.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        eyebrow: it.tag_label || "Tailored Solutions",
        title: it.title || "",
        body: it.body || "",
        tag: it.tag || "",
        img: it.media || "",
      }))
    : SOLUTIONS;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  // Dynamically compute the horizontal translate based on panel count.
  // For N panels: pan by (N-1)/N * 100%.
  const panCount = Math.max(1, liveSolutions.length);
  const panTarget = `-${((panCount - 1) / panCount) * 100}%`;
  const x = useTransform(scrollYProgress, [0, 1], ["0%", panTarget]);
  const progressLine = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    liveSolutions.forEach((s) => {
      if (!s.img) return;
      const im = new window.Image();
      im.src = s.img;
    });
  }, [liveSolutions]);

  return (
    <section
      id="tailored"
      ref={ref}
      data-testid="tailored-solutions"
      className="relative bg-[#0B0F14]"
      style={{ height: `${panCount * 100}vh` }}
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Top section eyebrow / progress bar */}
        <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 py-7 flex items-center justify-between gap-6 text-[0.7rem] font-bold tracking-[0.22em] uppercase">
          <span className="text-neutral-500">
            <span className="text-[#F4D64E]">Reel —</span> {progressLabel.replace(/^Reel\s*—\s*/, "")}
          </span>
          <div className="flex-1 max-w-[40vw] h-px bg-white/[0.08] relative overflow-hidden">
            <motion.span
              className="absolute inset-y-0 left-0 bg-[#F4D64E]"
              style={{ width: progressLine }}
            />
          </div>
          <span className="text-neutral-400 tabular-nums hidden sm:inline">
            01 — {String(panCount).padStart(2, "0")}
          </span>
        </div>

        {/* Horizontal track */}
        <motion.div
          style={{ x, width: `${liveSolutions.length * 100}vw` }}
          className="flex h-full will-change-transform"
        >
          {liveSolutions.map((s, i) => (
            <div
              key={i}
              className="relative w-screen h-screen shrink-0 flex items-center"
            >
              <div className="relative w-full h-full grid grid-cols-1 lg:grid-cols-12">
                {/* IMAGE half */}
                <div className="relative col-span-7 h-full overflow-hidden order-2 lg:order-1 hidden lg:block bg-[#0F141A]">
                  <img
                    src={s.img}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/30 via-transparent to-[#0B0F14]/0"
                  />
                  {/* Giant numeral overlay */}
                  <span
                    aria-hidden
                    className="absolute -bottom-10 -left-4 font-display font-black text-[#F4D64E]/[0.95] leading-[0.78] tracking-[-0.05em] mix-blend-difference"
                    style={{ fontSize: "clamp(10rem, 22vw, 24rem)" }}
                  >
                    {s.n}
                  </span>
                </div>

                {/* COPY half */}
                <div className="col-span-5 relative h-full flex items-center bg-gradient-to-br from-[#0F141A] to-[#0B0F14] order-1 lg:order-2 px-6 md:px-10 lg:px-14 xl:px-20 py-20">
                  {/* Mobile-only background image */}
                  <span
                    aria-hidden
                    className="lg:hidden absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: `url(${s.img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <span
                    aria-hidden
                    className="lg:hidden absolute inset-0 bg-gradient-to-b from-[#0B0F14]/70 via-[#0B0F14]/85 to-[#0B0F14]"
                  />

                  <div className="relative max-w-xl">
                    <div className="flex items-center gap-3 mb-7">
                      <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                        {s.n} / 04
                      </span>
                      <span className="h-px flex-1 bg-white/[0.10] max-w-[80px]" />
                      <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
                        {s.eyebrow}
                      </span>
                    </div>
                    <h3 className="font-display text-[2rem] sm:text-[2.6rem] md:text-[3.25rem] xl:text-[3.75rem] font-black tracking-[-0.04em] leading-[1.0] text-white">
                      {s.title}
                    </h3>
                    <p className="mt-8 text-[1rem] md:text-[1.1rem] text-neutral-300 leading-[1.65] max-w-[42ch]">
                      {s.body}
                    </p>
                    <p className="mt-8 italic text-[0.95rem] md:text-[1.05rem] text-[#F4D64E]/95 max-w-[42ch]">
                      {s.tag}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Drag hint — bottom-right */}
        <div className="absolute bottom-7 right-6 md:right-10 z-30 hidden md:flex items-center gap-3 text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          <span>Scroll to advance</span>
          <ArrowRight className="w-4 h-4 text-[#F4D64E]" />
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   03 · Stats band — count-up on intersection
============================================================ */
const STATS = [
  { value: 10, label: "Years of brand building" },
  { value: 261, label: "Projects completed" },
  { value: 15, label: "Team members" },
];

const CountUp = ({ to, duration = 1600 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {n}
    </span>
  );
};

const StatsBand = () => {
  const cms = useContent("creative");
  const s = cms?.stats_band || {};
  const eyebrow = s.eyebrow || "Our vision";
  const headlineMain = s.headline_main || "A decade of";
  const headlineItalic = s.headline_italic || "brand";
  const headlineAccent = s.headline_accent || "building";
  const description = s.description ||
    "Powerful, conversion-focused design — built once, ready to use, made to work beautifully together. These are just the numbers; the work speaks louder.";
  const liveStats = Array.isArray(s.items) && s.items.length
    ? s.items.map((it) => ({
        value: parseInt(it.title, 10) || 0,
        suffix: it.suffix || "",
        label: it.body || "",
      }))
    : STATS.map((st) => ({
        value: st.value,
        suffix: st.value === 10 ? "+" : "",
        label: st.label,
      }));
  return (
  <section
    data-testid="creative-stats"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 items-end mb-16 md:mb-20">
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3.25rem] lg:text-[4rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.65]"
          >
            <span>{description}</span>
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-3 gap-y-12 gap-x-10 border-t border-white/[0.08] pt-12">
        {liveStats.map((s, i) => (
          <li
            key={i}
            data-reveal="mask-block"
            style={{ "--i": i }}
            className="flex flex-col gap-4"
          >
            <span className="font-display text-[4.5rem] md:text-[6rem] lg:text-[7rem] font-black tracking-[-0.04em] leading-[0.92] text-[#F4D64E]">
              <CountUp to={s.value} />
              {s.suffix && <span>{s.suffix}</span>}
            </span>
            <span className="text-[0.75rem] font-bold tracking-[0.3em] uppercase text-neutral-400">
              {s.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   04 · Possibilities Lab — 6 asymmetric tiles, hover to play
============================================================ */
const POSSIBILITIES = [
  {
    n: "01",
    title: "Gift with purchase",
    body: "A smart sales enhancer designed to drive conversions by offering perceived value, increasing AOV, and encouraging repeat purchases.",
    img: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/4622417/4622417-uhd_2560_1440_25fps.mp4",
    span: "lg:col-span-5 lg:row-span-2",
  },
  {
    n: "02",
    title: "Bundling",
    body: "A smooth, interactive bundle builder — customize and preview product sets in real time with dynamic pricing and one-click cart.",
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/4068314/4068314-uhd_2560_1440_25fps.mp4",
    span: "lg:col-span-7",
  },
  {
    n: "03",
    title: "Quiz & personality test",
    body: "Multi-step UI flow that blends sleek UX with logic-driven personalization — ending in a tailored product suggestion.",
    img: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/3209663/3209663-uhd_2560_1440_25fps.mp4",
    span: "lg:col-span-4",
  },
  {
    n: "04",
    title: "Variant swatch",
    body: "Real-time preview of how a product looks in any option — click to switch, no reload, no friction.",
    img: "https://images.unsplash.com/photo-1485518882345-15568b007407?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/4109016/4109016-uhd_2560_1440_25fps.mp4",
    span: "lg:col-span-3",
  },
  {
    n: "05",
    title: "Upsell configurator",
    body: "Cross-sell strategy in your control — recommended products dynamically loaded, complementary items added straight from cart.",
    img: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/3209211/3209211-uhd_2560_1440_25fps.mp4",
    span: "lg:col-span-7 lg:row-span-2",
  },
  {
    n: "06",
    title: "Product configurator",
    body: "Smooth, interactive Shopify setup — instant updates, zero reloads, every screen size handled. Optimized to guide users from interest to purchase.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=70",
    video: "https://videos.pexels.com/video-files/3015527/3015527-uhd_2732_1440_25fps.mp4",
    span: "lg:col-span-5",
  },
];

const PossibilityTile = ({ item, i }) => {
  const videoRef = useRef(null);
  const hasVideo = !!(item.video && String(item.video).trim());
  const handleEnter = () => {
    if (!hasVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  };
  const handleLeave = () => {
    if (!hasVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
  };
  return (
    <li
      data-reveal="mask-block"
      style={{ "--i": Math.min(i, 5) }}
      data-testid={`possibility-${item.n}`}
      className={`relative group rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0F141A] ${item.span}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[16rem]">
        {/* Image — inset slightly negative so it overhangs the rounded
            clip on every side. This guarantees no sub-pixel sliver of
            uncovered image is exposed at the rounded corners during
            the hover scale transform. */}
        <img
          src={item.img}
          alt=""
          loading="lazy"
          className={`absolute -inset-[2px] w-[calc(100%+4px)] h-[calc(100%+4px)] object-cover transition-all duration-700 ease-out ${
            hasVideo
              ? "group-hover:opacity-0"
              : "group-hover:scale-[1.06]"
          }`}
        />
        {hasVideo ? (
          <video
            ref={videoRef}
            src={item.video}
            muted
            loop
            playsInline
            preload="none"
            className="absolute -inset-[2px] w-[calc(100%+4px)] h-[calc(100%+4px)] object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        ) : null}
        <span
          aria-hidden
          className="absolute -inset-[2px] bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/40 to-transparent"
        />
        <span
          aria-hidden
          className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/30 rounded-2xl transition-all duration-500"
        />

        {/* Top-left number tag */}
        <span className="absolute top-5 left-5 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
          {item.n}
        </span>

        {/* Hover-only play chip — only when there's a video to play */}
        {hasVideo ? (
          <span className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
            <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
          </span>
        ) : null}

        {/* Bottom copy */}
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          <h3 className="font-display text-[1.4rem] md:text-[1.7rem] lg:text-[2rem] font-black tracking-[-0.025em] leading-[1.1] text-white group-hover:text-[#F4D64E] transition-colors mb-3">
            {item.title}
          </h3>
          <p className="text-[0.92rem] md:text-[0.98rem] text-neutral-300 leading-[1.55] max-w-[44ch]">
            {item.body}
          </p>
        </div>
      </div>
    </li>
  );
};

const PossibilitiesLab = () => {
  const cms = useContent("creative");
  const p = cms?.possibilities_lab || {};
  const eyebrow = p.eyebrow || "Possibilities Lab";
  const headlineMain = p.headline_main || "Smart selling";
  const headlineItalic = p.headline_italic || "starts";
  const headlineAccent = p.headline_accent || "here";
  const description = p.description ||
    "Conversion-focused features built once and made to work beautifully together. A few examples below — if you can dream it, we can build it. Hover to play.";
  // Tile resolution — same gating principle as Creative Capabilities
  // on home: while CMS data is in-flight, do NOT render the hardcoded
  // POSSIBILITIES array (its media URLs would briefly fetch + cache).
  // Show a clean layout-only skeleton instead. Once CMS resolves we
  // render real items (or the permanent hardcoded fallback if the
  // admin never configured this section).
  const hasCmsTiles = Array.isArray(p.items) && p.items.length > 0;
  const liveTiles = hasCmsTiles
    ? p.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
        img: it.media || "",
        video: it.video || "",
        span: it.span || POSSIBILITIES[i]?.span || "lg:col-span-6",
      }))
    : !cms.__ready
      ? POSSIBILITIES.map((t) => ({ ...t, img: "", video: "" }))
      : POSSIBILITIES;
  return (
  <section
    data-testid="possibilities"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 mb-16 md:mb-20 items-end">
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3.25rem] lg:text-[4rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.65]"
          >
            <span>{description}</span>
          </p>
        </div>
      </div>

      <ul
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 lg:auto-rows-[18rem]"
      >
        {liveTiles.map((p, i) => (
          <PossibilityTile key={i} item={p} i={i} />
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   05 · More Services — 4 alternating panels with parallax media
============================================================ */
const SERVICES = [
  {
    n: "01",
    eyebrow: "Email marketing",
    title: "Your most overlooked growth channel — optimized.",
    body: "From planning to post-launch, end-to-end campaigns powered by segmentation, automation, and personalization. Built to drive engagement, conversions, and long-term loyalty — not just inbox placement.",
    cta: "Discover solutions",
    img: "https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1600&q=70",
  },
  {
    n: "02",
    eyebrow: "Social ads",
    title: "Noise isn't the goal — impact is.",
    body: "No two platforms are the same and neither are our strategies. Creative and targeting tailored to each channel, rapid experimentation, multivariate testing, and data-backed decisions that compound ROI.",
    cta: "Learn more",
    img: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=1600&q=70",
  },
  {
    n: "03",
    eyebrow: "Content creation",
    title: "Connect, convert, and cultivate loyal customers.",
    body: "Social posts, blog articles, on-site merchandising — every asset crafted with purpose. Full-funnel storytelling that combines culture, data, and brand voice to keep you relevant and consistently engaging.",
    cta: "Discover more",
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1600&q=70",
  },
  {
    n: "04",
    eyebrow: "Multimedia",
    title: "Visual storytelling that sells.",
    body: "Professional photography, film, and motion — from website content and product hero shots to dynamic social videos. High-quality visuals built to captivate and convert across every channel.",
    cta: "Contact us",
    img: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&w=1600&q=70",
  },
];

const VIDEO_EXT_RE = /\.(mp4|webm|mov|ogg|ogv|m4v)(\?|$)/i;
const isVideoUrl = (url) => typeof url === "string" && VIDEO_EXT_RE.test(url);

const ServicePanel = ({ item, i, total, onContact }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Mild image parallax
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const isOdd = i % 2 === 1;
  const totalStr = String(total || 4).padStart(2, "0");
  const mediaIsVideo = isVideoUrl(item.img);

  return (
    <div
      ref={ref}
      data-testid={`service-panel-${item.n}`}
      className="relative bg-[#0B0F14] border-b border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 items-center ${
            isOdd ? "lg:[direction:rtl]" : ""
          }`}
        >
          {/* MEDIA */}
          <div
            className={`lg:col-span-7 [direction:ltr] relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden rounded-2xl border border-white/[0.06]`}
          >
            {mediaIsVideo ? (
              <motion.video
                src={item.img}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                style={{ y }}
                className="absolute inset-[-6%] w-[112%] h-[112%] object-cover"
              />
            ) : (
              <motion.img
                src={item.img}
                alt=""
                loading="lazy"
                style={{ y }}
                className="absolute inset-[-6%] w-[112%] h-[112%] object-cover"
              />
            )}
            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/65 via-transparent to-transparent"
            />
            {/* Floating numeric tag */}
            <span className="absolute top-6 left-6 text-[0.72rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
              {item.n} / {totalStr}
            </span>
          </div>

          {/* COPY */}
          <div className="lg:col-span-5 [direction:ltr]">
            <span data-reveal="eyebrow">
              <Eyebrow>{item.eyebrow}</Eyebrow>
            </span>
            <h3
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[1.85rem] sm:text-[2.4rem] md:text-[2.85rem] lg:text-[3.25rem] font-black tracking-[-0.035em] leading-[1.02] text-white"
            >
              <span>{item.title}</span>
            </h3>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.65] max-w-[52ch]"
            >
              <span>{item.body}</span>
            </p>
            <div data-reveal="eyebrow" style={{ "--i": 3 }} className="mt-9">
              <button
                type="button"
                onClick={onContact}
                data-testid={`service-cta-${item.n}`}
                className="group inline-flex items-center gap-3 text-[0.78rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
              >
                {item.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MoreServices = ({ onContact }) => {
  const cms = useContent("creative");
  const m = cms?.more_services || {};
  const eyebrow = m.eyebrow || "More services we're great at";
  const headlineMain = m.headline_main || "Beyond the build —";
  const headlineItalic = m.headline_italic || "an";
  const headlineAccent = m.headline_accent || "entire growth stack";
  const liveServices = Array.isArray(m.items) && m.items.length
    ? m.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        eyebrow: it.tag_label || "",
        title: it.title || "",
        body: it.body || "",
        cta: it.cta || "Learn more",
        img: it.media || "",
      }))
    : SERVICES;
  return (
  <section
    data-testid="more-services"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32">
      <div className="max-w-3xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.25rem] md:text-[3.25rem] lg:text-[4rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
        >
          <span>
            {headlineMain}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
              </>
            ) : null}
            <PillarAccent>{headlineAccent}</PillarAccent>.
          </span>
        </h2>
      </div>
    </div>

    <div className="mt-20">
      {liveServices.map((s, i) => (
        <ServicePanel key={i} item={s} i={i} total={liveServices.length} onContact={onContact} />
      ))}
    </div>
  </section>
  );
};

/* ============================================================
   06 · Latest Projects — trophy tiles
============================================================ */
const PROJECTS = [
  {
    n: "01",
    title: "Café Joyeux",
    sub: "Coffee on a mission",
    img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1400&q=75",
  },
  {
    n: "02",
    title: "Veronique Gabai",
    sub: "Perfume & fragrances",
    img: "https://images.unsplash.com/photo-1541643600914-78b084683601?auto=format&fit=crop&w=1400&q=75",
  },
  {
    n: "03",
    title: "Madam Glam",
    sub: "Gel & nail polish, art",
    img: "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=1400&q=75",
  },
  {
    n: "04",
    title: "Ming Yu Wang",
    sub: "Designer statement jewelry",
    img: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=75",
  },
];

const LatestProjects = () => {
  const cms = useContent("creative");
  const lp = cms?.latest_projects || {};
  const eyebrow = lp.eyebrow || "Latest projects";
  const headlineMain = lp.headline_main || "The receipts.";
  const headlineItalic = lp.headline_italic || "In one";
  const headlineAccent = lp.headline_accent || "place";
  const ctaLabel = lp.cta_label || "All projects";
  const ctaHref = lp.cta_href || "#contact";
  const liveProjects = Array.isArray(lp.items) && lp.items.length
    ? lp.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        title: it.title || "",
        sub: it.body || "",
        href: it.href || "#",
        img: it.media || "",
      }))
    : PROJECTS.map((p) => ({ ...p, href: "#" }));
  return (
  <section
    data-testid="latest-projects"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 mb-16 md:mb-20 items-end">
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.5rem] md:text-[3.75rem] lg:text-[4.75rem] font-black tracking-[-0.04em] leading-[0.96] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-5 lg:text-right">
          <a
            data-reveal="eyebrow"
            style={{ "--i": 2 }}
            href={ctaHref}
            className="inline-flex items-center gap-3 text-[0.8rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] hover:text-white transition-colors"
          >
            {ctaLabel}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16 md:gap-y-24">
        {liveProjects.map((p, i) => (
          <li
            key={i}
            data-reveal="mask-block"
            style={{ "--i": Math.min(i, 3) }}
            data-testid={`project-${p.n}`}
            className={`group ${i % 2 === 1 ? "md:translate-y-20" : ""}`}
          >
            <a href={p.href || "#"} className="block">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/[0.06] mb-6">
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/70 via-transparent to-transparent"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/30 rounded-2xl transition-all duration-500"
                />
                <span className="absolute top-6 left-6 text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                  {p.n}
                </span>
                <span className="absolute top-6 right-6 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              <h3 className="font-display text-[1.6rem] md:text-[2rem] lg:text-[2.25rem] font-black tracking-[-0.025em] leading-[1.1] text-white group-hover:text-[#F4D64E] transition-colors">
                {p.title}
              </h3>
              <p className="mt-2 text-[0.92rem] md:text-[1rem] text-neutral-400 italic">
                {p.sub}
              </p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   07 · Cross-page browser — closes the loop with the rest of
   the site. 5 mosaic tiles at varied sizes pulled from the
   official site's "what's next" navigator. Each links to its
   route and previews the destination via a single-word verb.
============================================================ */
const BROWSE = [
  {
    label: "Sunday.",
    line: "Digital Solutions",
    href: "/",
    img: "https://images.unsplash.com/photo-1614728263952-84ea256f9679?auto=format&fit=crop&w=1200&q=70",
    span: "lg:col-span-4 lg:row-span-2",
  },
  {
    label: "Ecommerce",
    line: "Powering Stores",
    href: "/shopify-experts-development",
    img: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=70",
    span: "lg:col-span-4",
  },
  {
    label: "Marketing",
    line: "Streamlined Growth",
    href: "/ecommerce-marketing-agency",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=70",
    span: "lg:col-span-4",
  },
  {
    label: "Showcase",
    line: "Vision. Action. Result.",
    href: "/",
    img: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1200&q=70",
    span: "lg:col-span-4",
  },
  {
    label: "Let's talk",
    line: "And Build What's Next",
    href: "#contact",
    isCta: true,
    img: "https://images.unsplash.com/photo-1481487196290-c152efe083f5?auto=format&fit=crop&w=1200&q=70",
    span: "lg:col-span-4",
  },
];

const BrowseTile = ({ tile, i, onCta }) => {
  const inner = (
    <div
      className={`relative h-full min-h-[16rem] lg:min-h-[18rem] rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F141A] group transition-all duration-500 hover:border-[#F4D64E]/40`}
    >
      <img
        src={tile.img}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-50 transition-all duration-700 group-hover:opacity-80 group-hover:scale-105"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-[#0B0F14]/95 via-[#0B0F14]/70 to-[#0B0F14]/40 group-hover:from-[#0B0F14]/85 group-hover:via-[#0B0F14]/55 transition-colors duration-500"
      />
      {tile.isCta && (
        <span
          aria-hidden
          className="absolute -top-32 -right-20 w-[26rem] h-[26rem] rounded-full bg-[#F4D64E]/[0.18] blur-3xl"
        />
      )}

      {/* Content */}
      <div className="relative h-full flex flex-col justify-between p-7 md:p-9">
        <div className="flex items-start justify-between gap-4">
          <span className="text-[0.65rem] font-bold tracking-[0.3em] uppercase text-neutral-500 tabular-nums">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white group-hover:bg-[#F4D64E] group-hover:text-neutral-950 group-hover:border-[#F4D64E] transition-all duration-500">
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
        <div>
          <div className="font-display text-[1.7rem] md:text-[2.1rem] lg:text-[2.4rem] font-black tracking-[-0.025em] leading-[1.0] text-white group-hover:text-[#F4D64E] transition-colors mb-2">
            {tile.label}
          </div>
          <div className="text-[0.92rem] md:text-[1rem] text-neutral-300 italic">
            {tile.line}
          </div>
        </div>
      </div>
    </div>
  );

  if (tile.isCta) {
    return (
      <li
        data-reveal="mask-block"
        style={{ "--i": Math.min(i, 4) }}
        data-testid={`browse-tile-${i + 1}`}
        className={`${tile.span}`}
      >
        <button
          type="button"
          onClick={onCta}
          className="block w-full h-full text-left"
        >
          {inner}
        </button>
      </li>
    );
  }
  return (
    <li
      data-reveal="mask-block"
      style={{ "--i": Math.min(i, 4) }}
      data-testid={`browse-tile-${i + 1}`}
      className={`${tile.span}`}
    >
      <Link to={tile.href} className="block h-full">
        {inner}
      </Link>
    </li>
  );
};

const CrossPageBrowser = ({ onCta }) => {
  const cms = useContent("creative");
  const cp = cms?.cross_page_browser || {};
  const eyebrow = cp.eyebrow || "Browse the studio";
  const headlineMain = cp.headline_main || "Where would you";
  const headlineItalic = cp.headline_italic || "like to";
  const headlineAccent = cp.headline_accent || "go next";
  const description = cp.description ||
    "Same studio, different lens. Pick your path — or jump straight to a conversation.";
  const liveTiles = Array.isArray(cp.items) && cp.items.length
    ? cp.items.map((it, i) => ({
        label: it.title || "",
        line: it.body || "",
        href: it.href || "/",
        img: it.media || "",
        isCta: (it.is_cta || "").toString().toLowerCase() === "yes",
        span: it.span || BROWSE[i]?.span || "lg:col-span-4",
      }))
    : BROWSE;
  return (
  <section
    data-testid="cross-page-browser"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10 mb-14 md:mb-16 items-end">
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3.25rem] lg:text-[4rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>?
            </span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1rem] md:text-[1.1rem] text-neutral-400 leading-[1.65]"
          >
            <span>{description}</span>
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 lg:auto-rows-[18rem]">
        {liveTiles.map((tile, i) => (
          <BrowseTile key={i} tile={tile} i={i} onCta={onCta} />
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function CreativePage() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/creative",
    fallback: {
      title: "Creative & Design — Sunday Digital",
      description: "Editorial design, motion, photography, and multimedia for ambitious eCommerce brands.",
    },
  });
  useEffect(() => {
    document.title = "Creative & Design — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer — same as HomeV5 / ShopifyDev / Marketing
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="creative-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <CreativeHero onPrimary={openContact} />
        <TailoredSolutions />
        <StatsBand />
        <PossibilitiesLab />
        <MoreServices onContact={openContact} />
        <LatestProjects />
        <CrossPageBrowser onCta={openContact} />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
